import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import '../styles/Dashboard.css';
import { auth, db, storage } from '../firebase/firebase';
import { collection, addDoc, getDocs, doc, deleteDoc, updateDoc, query, where, setDoc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import WIcon from '../components/shared/WIcon';
import Modal from '../components/shared/Modal';

const VENDOR_PALETTE = ['#3b82f6', '#00bf63', '#8b5cf6', '#f59e0b', '#ef4444', '#10b981', '#0ea5e9'];
const initialsFor = (name) => (name || '—').split(/\s+/).filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase();

const emptyForm = { name: '', description: '', logo: null, email: '' };

const CompanyFormModal = ({ initialData, mode, onClose, onSaved }) => {
  const [formData, setFormData] = useState(initialData || emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const isEdit = mode === 'edit';

  const setField = (k) => (e) => {
    setFormData((f) => ({ ...f, [k]: e.target.value }));
    if (error) setError('');
  };

  const handleFile = (e) => {
    if (e.target.files[0]) setFormData((f) => ({ ...f, logo: e.target.files[0] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      let logoUrl = '';
      if (formData.logo) {
        const logoRef = ref(storage, `company-logos/${Date.now()}_${formData.logo.name}`);
        await uploadBytes(logoRef, formData.logo);
        logoUrl = await getDownloadURL(logoRef);
      }

      if (isEdit) {
        const companyRef = doc(db, 'companies', initialData.id);
        const updateData = {
          name: formData.name,
          description: formData.description,
          logoUrl: logoUrl || initialData.logoUrl || '',
          updatedAt: new Date(),
        };
        if (formData.email) updateData.email = formData.email.toLowerCase().trim();
        await updateDoc(companyRef, updateData);
      } else {
        if (!formData.email) {
          throw new Error('Admin email is required.');
        }
        const email = formData.email.toLowerCase().trim();

        // Reject if this email is already whitelisted as a company admin.
        const existingAdmin = await getDoc(doc(db, 'companyAdmins', email));
        if (existingAdmin.exists()) {
          throw new Error('This email is already a company admin.');
        }

        // Reject if a company with this admin email already exists.
        const dupCompany = await getDocs(query(collection(db, 'companies'), where('email', '==', email)));
        if (!dupCompany.empty) {
          throw new Error('This email is already in use by another company.');
        }

        // 1. Create the company doc.
        const companyDocRef = await addDoc(collection(db, 'companies'), {
          name: formData.name,
          description: formData.description,
          logoUrl: logoUrl || '',
          email,
          employeeCount: 0,
          createdAt: new Date(),
        });

        // 2. Whitelist the admin email — keyed by lowercase email, like superAdmins.
        await setDoc(doc(db, 'companyAdmins', email), {
          email,
          companyId: companyDocRef.id,
          name: `${formData.name} Admin`,
          role: 'company_admin',
          permissions: ['manage_employees', 'view_reports', 'company_settings'],
          addedAt: new Date(),
          addedBy: auth.currentUser?.email || null,
        });
      }

      onSaved?.();
      onClose?.();
    } catch (err) {
      console.error(err);
      setError(err.message || 'Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={isEdit ? 'Edit Company' : 'Add Company'}
      onClose={onClose}
      width={480}
    >
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {error && <div className="loysta-alert error">{error}</div>}

        <div className="form-group">
          <label>Company Name</label>
          <input
            type="text"
            className="form-control"
            value={formData.name}
            onChange={setField('name')}
            placeholder="e.g. Acme Corporation"
            required
          />
        </div>

        <div className="form-group">
          <label>Description</label>
          <textarea
            className="form-control"
            value={formData.description}
            onChange={setField('description')}
            placeholder="Brief description"
            rows="2"
          />
        </div>

        <div className="form-group">
          <label>Logo</label>
          <input type="file" accept="image/*" onChange={handleFile} className="form-control" />
        </div>

        <div className="form-group">
          <label>Admin Google email{isEdit ? '' : ' *'}</label>
          <input
            type="email"
            className="form-control"
            value={formData.email}
            onChange={setField('email')}
            placeholder="admin@company.com"
            required={!isEdit}
          />
          <span className="form-hint">
            {isEdit
              ? 'Leave blank to keep existing'
              : 'Whitelisted for Google sign-in. The admin signs in with this Google account to access the company portal.'}
          </span>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button type="button" className="cancel-btn" onClick={onClose} style={{ flex: 1 }}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={submitting} style={{ flex: 1, justifyContent: 'center' }}>
            {submitting ? 'Saving…' : isEdit ? 'Save changes' : 'Add Company'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

const Companies = () => {
  const { registerPageContext } = useOutletContext();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalState, setModalState] = useState({ open: false, mode: 'add', initialData: null });
  const [search, setSearch] = useState('');

  const fetchCompanies = useCallback(async () => {
    try {
      const snap = await getDocs(collection(db, 'companies'));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      await Promise.all(list.map(async (c) => {
        const usersSnap = await getDocs(query(collection(db, 'users'), where('companyId', '==', c.id)));
        c.employeeCount = usersSnap.size;
        try { await updateDoc(doc(db, 'companies', c.id), { employeeCount: usersSnap.size }); } catch (_) { /* ignore */ }
      }));

      setCompanies(list);
    } catch (err) {
      console.error('Error fetching companies:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

  // Register topbar Add CTA
  useEffect(() => {
    registerPageContext({
      addLabel: 'Add Company',
      onAdd: () => setModalState({ open: true, mode: 'add', initialData: null }),
    });
  }, [registerPageContext]);

  const handleEdit = (company) => {
    setModalState({
      open: true,
      mode: 'edit',
      initialData: {
        id: company.id,
        name: company.name || '',
        description: company.description || '',
        logo: null,
        logoUrl: company.logoUrl || '',
        email: company.email || '',
        password: '',
      },
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this company?')) return;
    try {
      await deleteDoc(doc(db, 'companies', id));
      fetchCompanies();
    } catch (err) {
      console.error('Error deleting company:', err);
    }
  };

  const filtered = companies.filter((c) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (c.name || '').toLowerCase().includes(s) || (c.email || '').toLowerCase().includes(s);
  });

  return (
    <>
      {modalState.open && (
        <CompanyFormModal
          mode={modalState.mode}
          initialData={modalState.initialData}
          onClose={() => setModalState({ open: false, mode: 'add', initialData: null })}
          onSaved={fetchCompanies}
        />
      )}

      <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: 7, padding: '7px 12px' }}>
            <WIcon name="search" size={15} color="#94a3b8" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search companies…"
              style={{ border: 'none', background: 'none', fontSize: 13, fontFamily: 'inherit', color: '#0f172a', outline: 'none', width: '100%' }}
            />
          </div>
          <span style={{ fontSize: 13, color: '#94a3b8' }}>{filtered.length} {filtered.length === 1 ? 'company' : 'companies'}</span>
          <button
            type="button"
            onClick={() => setModalState({ open: true, mode: 'add', initialData: null })}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', background: '#00bf63', color: 'white',
              border: 'none', borderRadius: 8,
              fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
              cursor: 'pointer', transition: 'background 0.15s',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#009950'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#00bf63'; }}
          >
            <WIcon name="plus" size={14} color="white" />
            Add Company
          </button>
        </div>

        <table className="data-table">
          <thead>
            <tr>
              <th>Company</th>
              <th>Description</th>
              <th>Admin Email</th>
              <th>Employees</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="6" style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Loading companies…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan="6" style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
                {companies.length === 0 ? 'No companies yet. Click "Add Company" to create one.' : 'No companies match your search.'}
              </td></tr>
            ) : filtered.map((c, i) => {
              const color = VENDOR_PALETTE[i % VENDOR_PALETTE.length];
              const isActive = (c.employeeCount || 0) > 0;
              return (
                <tr key={c.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {c.logoUrl ? (
                        <img src={c.logoUrl} alt={c.name} style={{ width: 32, height: 32, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                      ) : (
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: color + '20', color, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>{initialsFor(c.name)}</div>
                      )}
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{c.name || 'Unnamed'}</span>
                    </div>
                  </td>
                  <td style={{ color: '#64748b', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.description || '—'}</td>
                  <td style={{ color: '#334155' }}>{c.email || <span style={{ color: '#94a3b8' }}>Not set</span>}</td>
                  <td style={{ color: '#334155' }}>{c.employeeCount || 0}</td>
                  <td><span className={`loysta-status ${isActive ? 'active' : 'inactive'}`}>{isActive ? 'Active' : 'Inactive'}</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: 2 }}>
                      <button className="loysta-icon-btn" onClick={() => handleEdit(c)} title="Edit">
                        <WIcon name="edit" size={15} />
                      </button>
                      <button className="loysta-icon-btn danger" onClick={() => handleDelete(c.id)} title="Delete">
                        <WIcon name="trash" size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default Companies;
